import { useMemo } from 'react';
import type { Goal } from '@/types/goal';
import type { DailyReflection } from '@/types/reflection';
import { GoalMomentum } from './GoalMomentum';
import { CalendarDays, NotebookPen } from 'lucide-react';
import { format } from 'date-fns';
import {
  parseValuesTags,
  computeTopValue,
  filterReflectionsToLastNDays,
} from '@/lib/reflection-stats';

interface CompactWeeklySummaryProps {
  goals: Goal[];
  reflections?: DailyReflection[];
  onNavigateToGoals: () => void;
  onNavigateToHabits: () => void;
}

export const CompactWeeklySummary = ({
  goals,
  reflections,
  onNavigateToGoals,
  onNavigateToHabits,
}: CompactWeeklySummaryProps) => {
  // Calculate weekly habit completion rates (last 7 days)
  const weeklyStats = useMemo(() => {
    const days: { label: string; completed: number; total: number }[] = [];
    const today = new Date();

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayLabel = i === 0 ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'short' });

      let completed = 0;
      let total = 0;
      for (const goal of goals) {
        for (const habit of goal.habits) {
          total++;
          if (habit.completedDates.includes(dateStr)) {
            completed++;
          }
        }
      }

      days.push({ label: dayLabel, completed, total });
    }

    return days;
  }, [goals]);

  // Reflection stats for the week
  const reflectionStats = useMemo(() => {
    if (!reflections || reflections.length === 0) return null;

    const weekReflections = filterReflectionsToLastNDays(reflections, 7);
    // Only count reflections that have values_tags data
    const tagged = weekReflections.filter((r) => {
      const tags = parseValuesTags(r.valuesTags);
      return tags.expressed.length > 0 || tags.suppressed.length > 0;
    });

    if (tagged.length === 0) return null;

    const allExpressed: string[] = [];
    const allSuppressed: string[] = [];
    for (const r of tagged) {
      const tags = parseValuesTags(r.valuesTags);
      allExpressed.push(...tags.expressed);
      allSuppressed.push(...tags.suppressed);
    }

    return {
      daysReflected: weekReflections.length,
      topExpressed: computeTopValue(allExpressed),
      topSuppressed: computeTopValue(allSuppressed),
    };
  }, [reflections]);

  const totalHabits = goals.reduce((acc, g) => acc + g.habits.length, 0);

  if (totalHabits === 0 && goals.length === 0) return null;

  return (
    <div className="space-y-4">
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
      {/* Weekly habits bar chart */}
      <button
        onClick={onNavigateToHabits}
        className="p-4 rounded-xl bg-muted/30 border border-border/50 hover:border-primary/30 transition-all text-left opacity-80"
      >
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">This Week</span>
        </div>
        <div className="flex items-end gap-1 h-16">
          {weeklyStats.map((day, i) => {
            const pct = day.total > 0 ? (day.completed / day.total) * 100 : 0;
            const isToday = i === weeklyStats.length - 1;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full relative" style={{ height: '48px' }}>
                  <div
                    className={`absolute bottom-0 w-full rounded-sm transition-all ${
                      isToday
                        ? 'bg-primary'
                        : pct === 100
                          ? 'bg-emerald-500/60'
                          : pct > 0
                            ? 'bg-primary/30'
                            : 'bg-muted/40'
                    }`}
                    style={{ height: `${Math.max(pct, 4)}%` }}
                  />
                </div>
                <span className={`text-[10px] ${isToday ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                  {day.label.charAt(0)}
                </span>
              </div>
            );
          })}
        </div>
      </button>

      {/* Goal momentum */}
      <GoalMomentum goals={goals} onNavigateToGoals={onNavigateToGoals} />
    </div>

    {/* Reflection stats row */}
    {reflectionStats && (
      <div className="p-4 rounded-xl bg-muted/30 border border-border/50 opacity-80">
        <div className="flex items-center gap-2 mb-2">
          <NotebookPen className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Reflections</span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>{reflectionStats.daysReflected}/7 days</span>
          {reflectionStats.topExpressed && (
            <span>
              Top expressed:{' '}
              <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                {reflectionStats.topExpressed.name}
              </span>
            </span>
          )}
          {reflectionStats.topSuppressed && (
            <span>
              Top suppressed:{' '}
              <span className="text-orange-600 dark:text-orange-400 font-medium">
                {reflectionStats.topSuppressed.name}
              </span>
            </span>
          )}
        </div>
      </div>
    )}
    </div>
  );
};
